export const LINE_BROADCAST_MAX_BUBBLES = 5;
export const LINE_BROADCAST_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const LINE_BROADCAST_MAX_ALT_TEXT = 400;
export const LINE_BROADCAST_MAX_BUBBLE_TITLE = 80;
export const LINE_BROADCAST_MAX_BUBBLE_BODY = 500;
export const LINE_BROADCAST_MAX_BUTTON_LABEL = 20;
/** same as API LineBroadcastLimits.MaxExcludedUsers */
export const LINE_BROADCAST_MAX_EXCLUDED = 500;
/** same as API LineBroadcastLimits.MaxMessages — LINE ส่งได้ครั้งละ 5 ข้อความ */
export const LINE_BROADCAST_MAX_MESSAGES = 5;
export const LINE_BROADCAST_MAX_IMAGE_TEXT = 1000;
export const LINE_BROADCAST_MAX_NEWS = 5;

export type LineBroadcastMessageType = 'card' | 'image' | 'news';

export const LINE_BROADCAST_MESSAGE_TYPES: {
  value: LineBroadcastMessageType;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: 'image',
    label: 'ส่งรูปภาพ',
    description: 'รูปล้วน ไม่มีปุ่ม — แนบข้อความท้ายได้',
    icon: 'image',
  },
  {
    value: 'news',
    label: 'แจ้งประกาศในระบบ',
    description: 'เลือกประกาศที่เผยแพร่อยู่ กดแล้วเปิดหน้าประกาศใน LIFF',
    icon: 'campaign',
  },
  {
    value: 'card',
    label: 'การ์ดประกาศทั่วไป',
    description: 'ออกแบบการ์ดเอง แนบลิงก์ URL ได้',
    icon: 'view_carousel',
  },
];

export const LINE_BROADCAST_MESSAGE_TYPE_META: Record<LineBroadcastMessageType, { label: string; className: string; icon: string }> = {
  image: { label: 'รูปภาพ', className: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', icon: 'image' },
  news: { label: 'ประกาศในระบบ', className: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400', icon: 'campaign' },
  card: { label: 'การ์ด', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: 'view_carousel' },
};

/** card button — external URL or a page inside LIFF (`{liffUrl}?redirect={path}`) */
export type LineBroadcastLinkType = 'url' | 'liff';

export const LINE_BROADCAST_LIFF_PAGES: { path: string; label: string }[] = [
  { path: 'home', label: 'หน้าแรก' },
  { path: 'news', label: 'ข่าว / ประกาศ' },
  { path: 'my-member-card', label: 'บัตรสมาชิกของฉัน' },
  { path: 'option-register-renew', label: 'สมัคร / ต่ออายุบัตร' },
  { path: 'ev-charger', label: 'ชาร์จ EV' },
  { path: 'top-up', label: 'เติมเงิน' },
  { path: 'parking-history', label: 'ประวัติการจอด' },
];

const LIFF_PATH_PATTERN = /^[A-Za-z0-9_\-/=.]+$/;

export function normalizeLiffPath(path: string): string {
  return (path ?? '').trim().replace(/^\/+/, '');
}

/** `https://liff.line.me/{liffId}?redirect={path}` — same as API LiffLinkHelper.Build */
export function buildLiffLink(liffUrl: string, path: string): string {
  const base = (liffUrl ?? '').trim().replace(/\/+$/, '');
  const redirect = normalizeLiffPath(path);
  return redirect ? `${base}?redirect=${redirect}` : base;
}

/** liff.line.me/...?redirect=path → path (null = not an internal LIFF link) */
export function parseLiffPath(url: string): string | null {
  try {
    const parsed = new URL((url ?? '').trim());
    if (parsed.hostname !== 'liff.line.me') return null;
    const redirect = parsed.searchParams.get('redirect');
    return redirect !== null ? normalizeLiffPath(redirect) : null;
  } catch {
    return null;
  }
}

export interface LineBroadcastLinkSettingsDto {
  liffUrl?: string;
}

export type LineBroadcastAudienceType = 'all_members' | 'monthly_active';
export type LineBroadcastStatus = 'draft' | 'sending' | 'sent' | 'partial' | 'failed' | 'cancelled';

export const LINE_BROADCAST_AUDIENCES: {
  value: LineBroadcastAudienceType;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: 'all_members',
    label: 'สมาชิกทั้งหมด',
    description: 'สมาชิกที่ผูก LINE กับระบบ',
    icon: 'groups',
  },
  {
    value: 'monthly_active',
    label: 'สมาชิกรายเดือนที่ใช้งานอยู่',
    description: 'บัตรรายเดือนแบบเสียเงินที่ยังไม่หมดอายุ (ไม่รวมสมาชิกฟรี) · นับ 1 คนต่อ LINE',
    icon: 'card_membership',
  },
];

export const LINE_BROADCAST_STATUS_META: Record<LineBroadcastStatus, { label: string; className: string; icon: string }> = {
  draft: { label: 'ร่าง', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', icon: 'edit_note' },
  sending: { label: 'กำลังส่ง', className: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400', icon: 'send' },
  sent: { label: 'ส่งแล้ว', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: 'check_circle' },
  partial: { label: 'สำเร็จบางส่วน', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: 'warning' },
  failed: { label: 'ล้มเหลว', className: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400', icon: 'error' },
  cancelled: { label: 'ยกเลิก', className: 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500', icon: 'block' },
};

export const LINE_BROADCAST_STATUS_FILTERS: { value: LineBroadcastStatus | ''; label: string }[] = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'draft', label: 'ร่าง' },
  { value: 'sending', label: 'กำลังส่ง' },
  { value: 'sent', label: 'ส่งแล้ว' },
  { value: 'partial', label: 'สำเร็จบางส่วน' },
  { value: 'failed', label: 'ล้มเหลว' },
];

/* ---------- DTO (API) ---------- */

export interface LineBroadcastListItemDto {
  id?: number;
  title?: string;
  audienceType?: string;
  status?: string;
  recipientCount?: number;
  successCount?: number;
  failCount?: number;
  sentAt?: string | null;
  completedAt?: string | null;
  createDate?: string | null;
  writeDate?: string | null;
  createdByName?: string | null;
  sentByName?: string | null;
  messageType?: string | null;
}

export interface LineBroadcastTableResponse {
  total?: number;
  page?: number;
  pageSize?: number;
  items?: LineBroadcastListItemDto[];
}

export interface LineBroadcastBubbleDto {
  imagePath?: string | null;
  imageExt?: string | null;
  imageUrl?: string | null;
  title?: string;
  body?: string | null;
  buttonLabel?: string | null;
  buttonUrl?: string | null;
}

export interface LineBroadcastBatchDto {
  batchNo?: number;
  recipientCount?: number;
  status?: string;
  attemptCount?: number;
  httpStatus?: number | null;
  errorMessage?: string | null;
  sentAt?: string | null;
}

export interface LineBroadcastDetailDto extends LineBroadcastListItemDto {
  altText?: string;
  isEditable?: boolean;
  bubbles?: LineBroadcastBubbleDto[];
  quotaRemainingAtSend?: number | null;
  errorMessage?: string | null;
  lastTestAt?: string | null;
  batches?: LineBroadcastBatchDto[];
  siteIds?: number[];
  siteNames?: string[];
  excludedUserIds?: number[];
  images?: LineBroadcastImageDto[];
  imageText?: string | null;
  newsIds?: number[];
  news?: LineBroadcastNewsOptionDto[];
}

export interface LineBroadcastImageDto {
  imagePath?: string | null;
  imageExt?: string | null;
  imageUrl?: string | null;
}

export interface LineBroadcastNewsOptionDto {
  id?: number;
  title?: string;
  summary?: string | null;
  coverImageUrl?: string | null;
  categoryName?: string | null;
  siteId?: number | null;
  siteName?: string | null;
  publishFrom?: string | null;
  publishTo?: string | null;
  pin?: boolean;
  linkUrl?: string | null;
  isAvailable?: boolean;
}

export interface LineBroadcastAudienceSummaryDto {
  allMembers?: number;
  monthlyActive?: number;
  allMembersSelected?: number;
  monthlyActiveSelected?: number;
}

export interface LineBroadcastQuotaDto {
  isSuccess?: boolean;
  type?: string;
  limit?: number | null;
  used?: number;
  remaining?: number | null;
  tokenProfile?: string;
  message?: string | null;
}

export interface LineBroadcastImageUploadResponse {
  isSuccess?: boolean;
  message?: string;
  imagePath?: string | null;
  imageExt?: string | null;
  imageUrl?: string | null;
}

export interface LineBroadcastActionResponse {
  isSuccess?: boolean;
  message?: string;
  messageEn?: string;
  code?: string | null;
  idReturn?: number;
}

/* ---------- Payload ---------- */

export interface LineBroadcastBubblePayload {
  imagePath: string | null;
  imageExt: string | null;
  title: string;
  body: string | null;
  buttonLabel: string | null;
  buttonUrl: string | null;
}

export interface LineBroadcastSavePayload {
  userId: number;
  menuId: number;
  id: number;
  title: string;
  altText: string;
  audienceType: LineBroadcastAudienceType;
  bubbles: LineBroadcastBubblePayload[];
  siteIds: number[];
  excludedUserIds: number[];
  messageType: LineBroadcastMessageType;
  images: LineBroadcastImagePayload[];
  imageText: string | null;
  newsIds: number[];
}

export interface LineBroadcastImagePayload {
  imagePath: string;
  imageExt: string;
}

export interface LineBroadcastTestSendPayload {
  userId: number;
  menuId: number;
  id: number;
  altText: string;
  bubbles: LineBroadcastBubblePayload[];
  testLineUid?: string;
  messageType: LineBroadcastMessageType;
  images: LineBroadcastImagePayload[];
  imageText: string | null;
  newsIds: number[];
}

export interface LineBroadcastActionPayload {
  userId: number;
  menuId: number;
  id: number;
}

/* ---------- UI model ---------- */

export interface LineBroadcastListItem {
  id: number;
  title: string;
  audienceType: LineBroadcastAudienceType;
  status: LineBroadcastStatus;
  recipientCount: number;
  successCount: number;
  failCount: number;
  sentAt: string | null;
  createDate: string | null;
  createdByName: string;
  sentByName: string;
  messageType: LineBroadcastMessageType;
}

export interface LineBroadcastImage {
  uiKey: string;
  imagePath: string;
  imageExt: string;
  imageUrl: string;
}

export interface LineBroadcastNewsOption {
  id: number;
  title: string;
  summary: string;
  coverImageUrl: string;
  categoryName: string;
  siteId: number | null;
  siteName: string;
  publishFrom: string | null;
  publishTo: string | null;
  pin: boolean;
  linkUrl: string;
  isAvailable: boolean;
}

export interface LineBroadcastBubble {
  uiKey: string;
  imagePath: string;
  imageExt: string;
  imageUrl: string;
  title: string;
  body: string;
  buttonLabel: string;
  buttonUrl: string;
  linkType: LineBroadcastLinkType;
  /** linkType = liff — route in LIFF e.g. `ev-charger` */
  liffPath: string;
}

export interface LineBroadcastBatch {
  batchNo: number;
  recipientCount: number;
  status: string;
  attemptCount: number;
  httpStatus: number | null;
  errorMessage: string;
  sentAt: string | null;
}

export interface LineBroadcastDetail {
  id: number;
  title: string;
  altText: string;
  audienceType: LineBroadcastAudienceType;
  status: LineBroadcastStatus;
  isEditable: boolean;
  bubbles: LineBroadcastBubble[];
  recipientCount: number;
  successCount: number;
  failCount: number;
  quotaRemainingAtSend: number | null;
  errorMessage: string;
  sentAt: string | null;
  completedAt: string | null;
  lastTestAt: string | null;
  createdByName: string;
  sentByName: string;
  batches: LineBroadcastBatch[];
  /** monthly_active only — empty = every site */
  siteIds: number[];
  siteNames: string[];
  excludedUserIds: number[];
  messageType: LineBroadcastMessageType;
  images: LineBroadcastImage[];
  imageText: string;
  /** news mode — selected news in carousel order */
  news: LineBroadcastNewsOption[];
}

export interface LineBroadcastAudienceSummary {
  allMembers: number;
  monthlyActive: number;
  allMembersSelected: number;
  monthlyActiveSelected: number;
}

export interface LineBroadcastQuota {
  isSuccess: boolean;
  isUnlimited: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  tokenProfile: string;
}

/* ---------- Mappers ---------- */

let uiKeySeq = 0;
const nextUiKey = () => `bubble-${Date.now()}-${++uiKeySeq}`;

function toAudience(value?: string | null): LineBroadcastAudienceType {
  return value === 'monthly_active' ? 'monthly_active' : 'all_members';
}

function toStatus(value?: string | null): LineBroadcastStatus {
  const allowed: LineBroadcastStatus[] = ['draft', 'sending', 'sent', 'partial', 'failed', 'cancelled'];
  return allowed.includes(value as LineBroadcastStatus) ? (value as LineBroadcastStatus) : 'draft';
}

/** Same as API LineBroadcastMessageTypes.Normalize — unknown / legacy → card */
export function toLineBroadcastMessageType(value?: string | null): LineBroadcastMessageType {
  const v = (value ?? '').trim().toLowerCase();
  return v === 'image' || v === 'news' ? v : 'card';
}

export function createLineBroadcastImage(imagePath: string, imageExt: string, imageUrl: string): LineBroadcastImage {
  return { uiKey: `image-${Date.now()}-${++uiKeySeq}`, imagePath, imageExt, imageUrl };
}

export function mapLineBroadcastNewsOption(dto: LineBroadcastNewsOptionDto): LineBroadcastNewsOption {
  const id = Number(dto.id ?? 0);
  const siteId = Number(dto.siteId ?? 0);
  return {
    id,
    title: dto.title || `ประกาศ #${id}`,
    summary: dto.summary ?? '',
    coverImageUrl: dto.coverImageUrl ?? '',
    categoryName: dto.categoryName ?? '',
    siteId: siteId > 0 ? siteId : null,
    siteName: dto.siteName ?? '',
    publishFrom: dto.publishFrom ?? null,
    publishTo: dto.publishTo ?? null,
    pin: dto.pin === true,
    linkUrl: dto.linkUrl ?? '',
    isAvailable: dto.isAvailable !== false,
  };
}

export function createEmptyLineBroadcastBubble(): LineBroadcastBubble {
  return {
    uiKey: nextUiKey(),
    imagePath: '',
    imageExt: '',
    imageUrl: '',
    title: '',
    body: '',
    buttonLabel: '',
    buttonUrl: '',
    linkType: 'liff',
    liffPath: '',
  };
}

export function createEmptyLineBroadcastDetail(): LineBroadcastDetail {
  return {
    id: 0,
    title: '',
    altText: '',
    audienceType: 'all_members',
    status: 'draft',
    isEditable: true,
    bubbles: [createEmptyLineBroadcastBubble()],
    recipientCount: 0,
    successCount: 0,
    failCount: 0,
    quotaRemainingAtSend: null,
    errorMessage: '',
    sentAt: null,
    completedAt: null,
    lastTestAt: null,
    createdByName: '',
    sentByName: '',
    batches: [],
    siteIds: [],
    siteNames: [],
    excludedUserIds: [],
    messageType: 'image',
    images: [],
    imageText: '',
    news: [],
  };
}

export function mapLineBroadcastListItem(dto: LineBroadcastListItemDto): LineBroadcastListItem {
  return {
    id: Number(dto.id ?? 0),
    title: dto.title ?? '',
    audienceType: toAudience(dto.audienceType),
    status: toStatus(dto.status),
    recipientCount: Number(dto.recipientCount ?? 0),
    successCount: Number(dto.successCount ?? 0),
    failCount: Number(dto.failCount ?? 0),
    sentAt: dto.sentAt ?? null,
    createDate: dto.createDate ?? null,
    createdByName: dto.createdByName ?? '',
    sentByName: dto.sentByName ?? '',
    messageType: toLineBroadcastMessageType(dto.messageType),
  };
}

export function mapLineBroadcastDetail(dto: LineBroadcastDetailDto): LineBroadcastDetail {
  const bubbles = (dto.bubbles ?? []).map((b): LineBroadcastBubble => {
    const buttonUrl = b.buttonUrl ?? '';
    const liffPath = parseLiffPath(buttonUrl);
    return {
      uiKey: nextUiKey(),
      imagePath: b.imagePath ?? '',
      imageExt: b.imageExt ?? '',
      imageUrl: b.imageUrl ?? '',
      title: b.title ?? '',
      body: b.body ?? '',
      buttonLabel: b.buttonLabel ?? '',
      buttonUrl,
      linkType: liffPath !== null || !buttonUrl ? 'liff' : 'url',
      liffPath: liffPath ?? '',
    };
  });
  return {
    id: Number(dto.id ?? 0),
    title: dto.title ?? '',
    altText: dto.altText ?? '',
    audienceType: toAudience(dto.audienceType),
    status: toStatus(dto.status),
    isEditable: dto.isEditable ?? dto.status === 'draft',
    bubbles: bubbles.length ? bubbles : [createEmptyLineBroadcastBubble()],
    recipientCount: Number(dto.recipientCount ?? 0),
    successCount: Number(dto.successCount ?? 0),
    failCount: Number(dto.failCount ?? 0),
    quotaRemainingAtSend: dto.quotaRemainingAtSend ?? null,
    errorMessage: dto.errorMessage ?? '',
    sentAt: dto.sentAt ?? null,
    completedAt: dto.completedAt ?? null,
    lastTestAt: dto.lastTestAt ?? null,
    createdByName: dto.createdByName ?? '',
    sentByName: dto.sentByName ?? '',
    batches: (dto.batches ?? []).map((x) => ({
      batchNo: Number(x.batchNo ?? 0),
      recipientCount: Number(x.recipientCount ?? 0),
      status: x.status ?? '',
      attemptCount: Number(x.attemptCount ?? 0),
      httpStatus: x.httpStatus ?? null,
      errorMessage: x.errorMessage ?? '',
      sentAt: x.sentAt ?? null,
    })),
    siteIds: (dto.siteIds ?? []).map(Number).filter((id) => id > 0),
    siteNames: dto.siteNames ?? [],
    excludedUserIds: (dto.excludedUserIds ?? []).map(Number).filter((id) => id > 0),
    messageType: toLineBroadcastMessageType(dto.messageType),
    images: (dto.images ?? [])
      .filter((x) => !!x.imagePath)
      .map((x) => createLineBroadcastImage(x.imagePath ?? '', x.imageExt ?? '', x.imageUrl ?? '')),
    imageText: dto.imageText ?? '',
    news: (dto.news ?? []).map(mapLineBroadcastNewsOption).filter((x) => x.id > 0),
  };
}

export function mapLineBroadcastAudienceSummary(dto: LineBroadcastAudienceSummaryDto | null): LineBroadcastAudienceSummary {
  const allMembers = Number(dto?.allMembers ?? 0);
  const monthlyActive = Number(dto?.monthlyActive ?? 0);
  return {
    allMembers,
    monthlyActive,
    allMembersSelected: Number(dto?.allMembersSelected ?? allMembers),
    monthlyActiveSelected: Number(dto?.monthlyActiveSelected ?? monthlyActive),
  };
}

export function mapLineBroadcastQuota(dto: LineBroadcastQuotaDto | null): LineBroadcastQuota {
  return {
    isSuccess: dto?.isSuccess === true,
    isUnlimited: dto?.type === 'none',
    limit: dto?.limit ?? null,
    used: Number(dto?.used ?? 0),
    remaining: dto?.remaining ?? null,
    tokenProfile: dto?.tokenProfile ?? '',
  };
}

function toBubblePayload(bubble: LineBroadcastBubble, liffUrl: string): LineBroadcastBubblePayload {
  const trimOrNull = (value: string) => (value?.trim() ? value.trim() : null);
  const buttonUrl =
    bubble.linkType === 'liff'
      ? normalizeLiffPath(bubble.liffPath) ? buildLiffLink(liffUrl, bubble.liffPath) : null
      : trimOrNull(bubble.buttonUrl);
  return {
    imagePath: trimOrNull(bubble.imagePath),
    imageExt: trimOrNull(bubble.imageExt),
    title: bubble.title?.trim() ?? '',
    body: trimOrNull(bubble.body),
    buttonLabel: trimOrNull(bubble.buttonLabel),
    buttonUrl,
  };
}

/** Only the chosen mode's content is sent — API ToDesign drops the rest too. */
function toContentPayload(detail: LineBroadcastDetail, liffUrl: string) {
  const type = detail.messageType;
  return {
    messageType: type,
    bubbles: type === 'card' ? detail.bubbles.map((b) => toBubblePayload(b, liffUrl)) : [],
    images:
      type === 'image'
        ? detail.images.map((x) => ({ imagePath: x.imagePath, imageExt: x.imageExt }))
        : [],
    imageText: type === 'image' && detail.imageText.trim() ? detail.imageText.trim() : null,
    newsIds: type === 'news' ? detail.news.map((x) => x.id) : [],
  };
}

export function toLineBroadcastSavePayload(
  detail: LineBroadcastDetail,
  userId: number,
  menuId: number,
  liffUrl: string,
): LineBroadcastSavePayload {
  return {
    userId,
    menuId,
    id: detail.id,
    title: detail.title.trim(),
    altText: detail.altText.trim(),
    audienceType: detail.audienceType,
    siteIds: detail.audienceType === 'monthly_active' ? [...detail.siteIds] : [],
    excludedUserIds: [...detail.excludedUserIds],
    ...toContentPayload(detail, liffUrl),
  };
}

export function toLineBroadcastTestPayload(
  detail: LineBroadcastDetail,
  userId: number,
  menuId: number,
  testLineUid: string,
  liffUrl: string,
): LineBroadcastTestSendPayload {
  return {
    userId,
    menuId,
    id: detail.id,
    altText: detail.altText.trim(),
    testLineUid: testLineUid.trim() || undefined,
    ...toContentPayload(detail, liffUrl),
  };
}

/** card mode uses LIFF links but the LIFF URL setting is not loaded yet */
export function needsLiffUrl(detail: LineBroadcastDetail): boolean {
  return detail.messageType === 'card'
    && detail.bubbles.some((b) => b.linkType === 'liff' && !!normalizeLiffPath(b.liffPath));
}

/** image mode — 5 messages per request; the optional text takes one slot */
export function lineBroadcastMaxImages(detail: LineBroadcastDetail): number {
  return detail.imageText.trim() ? LINE_BROADCAST_MAX_MESSAGES - 1 : LINE_BROADCAST_MAX_MESSAGES;
}

/** Same rules as API LineBroadcastFlexBuilder (Validate / ValidateImages / ValidateNewsSelection). */
export function validateLineBroadcastDraft(detail: LineBroadcastDetail): string | null {
  if (!detail.title.trim()) return 'กรุณาระบุชื่อแคมเปญ';

  if (detail.messageType === 'image') {
    const max = lineBroadcastMaxImages(detail);
    if (!detail.images.length) return 'กรุณาอัปโหลดรูปอย่างน้อย 1 รูป';
    if (detail.images.length > max) {
      return detail.imageText.trim()
        ? `แนบข้อความแล้วส่งรูปได้ไม่เกิน ${max} รูป (LINE ส่งได้ครั้งละ ${LINE_BROADCAST_MAX_MESSAGES} ข้อความ)`
        : `ส่งรูปได้ไม่เกิน ${max} รูป`;
    }
    if (detail.imageText.trim().length > LINE_BROADCAST_MAX_IMAGE_TEXT) {
      return `ข้อความแนบยาวได้ไม่เกิน ${LINE_BROADCAST_MAX_IMAGE_TEXT} ตัวอักษร`;
    }
    return null;
  }

  if (!detail.altText.trim()) return 'กรุณาระบุข้อความแจ้งเตือน';
  if (detail.altText.trim().length > LINE_BROADCAST_MAX_ALT_TEXT) {
    return `ข้อความแจ้งเตือนยาวได้ไม่เกิน ${LINE_BROADCAST_MAX_ALT_TEXT} ตัวอักษร`;
  }

  if (detail.messageType === 'news') {
    if (!detail.news.length) return 'กรุณาเลือกประกาศอย่างน้อย 1 รายการ';
    if (detail.news.length > LINE_BROADCAST_MAX_NEWS) return `เลือกประกาศได้ไม่เกิน ${LINE_BROADCAST_MAX_NEWS} รายการ`;
    const unavailable = detail.news.find((x) => !x.isAvailable);
    if (unavailable) return `ประกาศ "${unavailable.title}" ไม่อยู่ในสถานะเผยแพร่แล้ว — นำออกก่อนส่ง`;
    return null;
  }

  if (!detail.bubbles.length) return 'กรุณาเพิ่มการ์ดอย่างน้อย 1 ใบ';
  if (detail.bubbles.length > LINE_BROADCAST_MAX_BUBBLES) return `การ์ดได้ไม่เกิน ${LINE_BROADCAST_MAX_BUBBLES} ใบ`;

  for (let i = 0; i < detail.bubbles.length; i++) {
    const b = detail.bubbles[i];
    const n = i + 1;
    if (!b.title.trim()) return `การ์ดที่ ${n}: กรุณาระบุหัวข้อ`;
    const hasLabel = !!b.buttonLabel.trim();
    if (b.linkType === 'liff') {
      const path = normalizeLiffPath(b.liffPath);
      if (hasLabel !== !!path) return `การ์ดที่ ${n}: ปุ่มต้องมีทั้งชื่อปุ่มและหน้าใน LIFF`;
      if (path && !LIFF_PATH_PATTERN.test(path)) {
        return `การ์ดที่ ${n}: path ใน LIFF ใช้ได้เฉพาะ a-z 0-9 - _ / (เช่น ev-charger)`;
      }
      continue;
    }
    const hasUrl = !!b.buttonUrl.trim();
    if (hasLabel !== hasUrl) return `การ์ดที่ ${n}: ปุ่มต้องมีทั้งชื่อปุ่มและลิงก์`;
    if (hasUrl && !/^(https|line):\/\//i.test(b.buttonUrl.trim())) {
      return `การ์ดที่ ${n}: ลิงก์ต้องขึ้นต้นด้วย https:// หรือ line://`;
    }
    if (hasUrl && parseLiffPath(b.buttonUrl) === null && /^https:\/\/liff\./i.test(b.buttonUrl.trim())) {
      return `การ์ดที่ ${n}: ลิงก์ภายใน LIFF ให้เลือก “หน้าใน LIFF” แทน`;
    }
  }
  return null;
}

/* ---------- Send progress ---------- */

export interface LineBroadcastProgressDto {
  id?: number;
  status?: string;
  recipientCount?: number;
  processedCount?: number;
  successCount?: number;
  failCount?: number;
  percent?: number;
  batchTotal?: number;
  batchDone?: number;
  isFinished?: boolean;
  errorMessage?: string | null;
}

export interface LineBroadcastProgress {
  id: number;
  status: LineBroadcastStatus;
  recipientCount: number;
  processedCount: number;
  successCount: number;
  failCount: number;
  percent: number;
  batchTotal: number;
  batchDone: number;
  isFinished: boolean;
  errorMessage: string;
}

export function mapLineBroadcastProgress(dto: LineBroadcastProgressDto | null): LineBroadcastProgress {
  return {
    id: Number(dto?.id ?? 0),
    status: toStatus(dto?.status),
    recipientCount: Number(dto?.recipientCount ?? 0),
    processedCount: Number(dto?.processedCount ?? 0),
    successCount: Number(dto?.successCount ?? 0),
    failCount: Number(dto?.failCount ?? 0),
    percent: Math.min(100, Math.max(0, Number(dto?.percent ?? 0))),
    batchTotal: Number(dto?.batchTotal ?? 0),
    batchDone: Number(dto?.batchDone ?? 0),
    isFinished: dto?.isFinished === true,
    errorMessage: dto?.errorMessage ?? '',
  };
}

/* ---------- Audience filter (site group + screening) ---------- */

export interface LineBroadcastSiteOptionDto {
  siteId?: number;
  siteName?: string;
  memberCount?: number;
}

export interface LineBroadcastSiteOption {
  siteId: number;
  siteName: string;
  memberCount: number;
}

export interface LineBroadcastAudienceMemberDto {
  cpUserId?: number;
  name?: string;
  phone?: string | null;
  siteNames?: string[];
  cardEndDate?: string | null;
  lastLogin?: string | null;
  cardCount?: number;
  subscriptionNames?: string[];
}

export interface LineBroadcastAudienceMembersResponse {
  total?: number;
  page?: number;
  pageSize?: number;
  items?: LineBroadcastAudienceMemberDto[];
}

export interface LineBroadcastAudienceMember {
  cpUserId: number;
  name: string;
  phone: string;
  siteNames: string[];
  cardEndDate: string | null;
  lastLogin: string | null;
  cardCount: number;
  subscriptionNames: string[];
}

export function mapLineBroadcastSiteOption(dto: LineBroadcastSiteOptionDto): LineBroadcastSiteOption {
  const siteId = Number(dto.siteId ?? 0);
  return {
    siteId,
    siteName: dto.siteName || `#${siteId}`,
    memberCount: Number(dto.memberCount ?? 0),
  };
}

export function mapLineBroadcastAudienceMember(dto: LineBroadcastAudienceMemberDto): LineBroadcastAudienceMember {
  const cpUserId = Number(dto.cpUserId ?? 0);
  return {
    cpUserId,
    name: dto.name || `สมาชิก #${cpUserId}`,
    phone: dto.phone ?? '',
    siteNames: dto.siteNames ?? [],
    cardEndDate: dto.cardEndDate ?? null,
    lastLogin: dto.lastLogin ?? null,
    cardCount: Number(dto.cardCount ?? 0),
    subscriptionNames: dto.subscriptionNames ?? [],
  };
}

/* ---------- Monthly member detail (paid active cards by uid = line_id) ---------- */

export interface LineBroadcastMemberCardDto {
  cardId?: number;
  cardCode?: string | null;
  cardName?: string | null;
  holderName?: string | null;
  subscriptionName?: string | null;
  siteName?: string | null;
  issueDate?: string | null;
  expireDate?: string | null;
  licenses?: string[];
}

export interface LineBroadcastMemberCardsResponse {
  cpUserId?: number;
  name?: string;
  phone?: string | null;
  lineId?: string | null;
  items?: LineBroadcastMemberCardDto[];
}

export interface LineBroadcastMemberCard {
  cardId: number;
  cardCode: string;
  cardName: string;
  holderName: string;
  subscriptionName: string;
  siteName: string;
  issueDate: string | null;
  expireDate: string | null;
  licenses: string[];
}

export interface LineBroadcastMemberCards {
  cpUserId: number;
  name: string;
  phone: string;
  lineId: string;
  items: LineBroadcastMemberCard[];
}

export function mapLineBroadcastMemberCards(dto: LineBroadcastMemberCardsResponse | null | undefined): LineBroadcastMemberCards {
  const cpUserId = Number(dto?.cpUserId ?? 0);
  return {
    cpUserId,
    name: dto?.name || `สมาชิก #${cpUserId}`,
    phone: dto?.phone ?? '',
    lineId: dto?.lineId ?? '',
    items: (dto?.items ?? []).map((c) => ({
      cardId: Number(c.cardId ?? 0),
      cardCode: c.cardCode ?? '',
      cardName: c.cardName ?? '',
      holderName: c.holderName ?? '',
      subscriptionName: c.subscriptionName ?? '',
      siteName: c.siteName ?? '',
      issueDate: c.issueDate ?? null,
      expireDate: c.expireDate ?? null,
      licenses: c.licenses ?? [],
    })),
  };
}
